import Role from "enums/Role";
import SettingPolicyType from "enums/SettingPolicyType";

export type ApprovalPolicy = {
  id: string;
  userRole: string;
  type: SettingPolicyType;
  updatedAt?: string;
  policy?: { approvalRoles?: Role[] | null } | null;
  /**
   * Read when prefilling the approvals form for an edit. No query selects it
   * and no caller passes a policy to that drawer, so it is never populated.
   */
  workflowSettings?: string;
};

type ApprovalCombination = {
  key: string;
  label: string;
  // Written as a whole-array replacement when the cell is picked.
  roles: Role[];
  // Kept as its own predicate rather than comparing against `roles`: the
  // stored array is not guaranteed to be one of these five, and matching on
  // set equality would silently blank a row the old checks would have ticked.
  matches: (roles: Role[]) => boolean;
  // The last column only exists on the workflow table; the others render an
  // empty cell so every section stays aligned with the shared colgroup.
  workflowOnly?: boolean;
};

export const APPROVAL_COMBINATIONS: ApprovalCombination[] = [
  {
    key: "none",
    label: "No Approval",
    roles: [],
    matches: (roles) => roles.length === 0,
  },
  {
    key: "manager",
    label: "Manager",
    roles: [Role.Manager],
    matches: (roles) =>
      roles.includes(Role.Manager) &&
      !roles.includes(Role.Admin) &&
      !roles.includes(Role.Accountant),
  },
  {
    key: "admin",
    label: "Admin",
    roles: [Role.Admin],
    matches: (roles) =>
      roles.includes(Role.Admin) &&
      !roles.includes(Role.Manager) &&
      !roles.includes(Role.Accountant),
  },
  {
    key: "adminManager",
    label: "Admin + Manager",
    roles: [Role.Manager, Role.Admin],
    matches: (roles) =>
      roles.includes(Role.Manager) && roles.includes(Role.Admin),
  },
  {
    key: "managerAccountant",
    label: "Manager + Accountant",
    roles: [Role.Manager, Role.Accountant],
    matches: (roles) =>
      roles.includes(Role.Manager) && roles.includes(Role.Accountant),
    workflowOnly: true,
  },
];

/**
 * A spend category inside a transaction policy, as the API returns it.
 * `isCategoryEnabled` is null on a category nobody has configured, which is the
 * null that made the row checkbox unclearable.
 */
export type CategorySetting = {
  categoryId: string;
  categoryName: string;
  amount: number;
  currency?: string | null;
  isCategoryEnabled?: boolean | null;
};

/**
 * A category edit the user has made but not saved. The amount is a string here
 * because it comes straight off a text field, and is parsed on submit.
 */
export type PendingCategorySetting = Omit<
  CategorySetting,
  "amount" | "isCategoryEnabled"
> & {
  /** A string off a text field, or the stored number when a bulk action set it. */
  amount: string | number;
  isCategoryEnabled: boolean;
};

/** One transaction policy: a type, for a role, with its categories. */
export type TransactionPolicy = {
  id: string;
  type: SettingPolicyType;
  userRole: string;
  updatedAt?: string;
  isPolicyTypeEnabled?: boolean | null;
  policy?: { categories?: CategorySetting[] | null } | null;
};
